import React from "react";
import type {
  DepGraph,
  PublishPreview,
  SectionChangesSummary,
  TaktColorEntry,
} from "../../../models";
import type { AnchorPublishStats } from "../PullPlanGrid/PullPlanGrid";
import type { LedgerSnapshot } from "../ScopeLedger/ScopeLedger";
import { formatDateMonthDay } from "../../../utils/format";
import LedgerTable from "../ScopeLedger/LedgerTable/LedgerTable";
import AnchorsBoard from "../AnchorsBoard/AnchorsBoard";
import WorkbenchRows from "../../shared/cascadeWorkbench/CascadeWorkbenchModal/WorkbenchRows/WorkbenchRows";
import {
  downstreamCascadeToWorkbench,
  downstreamCount,
  downstreamSectionMap,
} from "./cascadeToWorkbench";
import "./PublishSectionModal.css";

interface Props {
  sectionTitle: string;
  /** Left of the slim header — the same "← Sections" back the board shows, so
   *  the review reads as a pull page (not a generic modal). */
  headerLeft?: React.ReactNode;
  /** "N levels · M activities" — the board's header subtitle, mirrored here. */
  subtitle?: string;
  /** Outside ties (anchors): total / not yet reviewed / severed by this draft. */
  anchorStats?: AnchorPublishStats;
  /** Live staged dep graph — the Anchors tab renders the board's grouped
   *  AnchorsBoard (read-only) off it. Null until the board has loaded it. */
  anchorGraph?: DepGraph | null;
  /** Close the review and jump to the board's editable Anchors tab. */
  onEditAnchors?: () => void;
  preview: PublishPreview;
  /** Frozen copy of the board's scope ledger — the "What changed" table. Null
   *  when the grid never produced one (e.g. an empty plan). */
  ledger?: LedgerSnapshot | null;
  /** Show only changed rows in the "What changed" table (default true). */
  changesOnly?: boolean;
  onChangesOnlyChange?: (v: boolean) => void;
  colors: Record<string, TaktColorEntry>;
  activityWorkCodeMap: Map<number, string>;
  zoneMap: Map<number, string>;
  publishing: boolean;
  /** Publish to the live schedule. `applyDownstream=false` saves the plan's own
   *  work but leaves downstream activities untouched (resolved later via F9). */
  onPublish: (applyDownstream: boolean) => void;
  onCancel: () => void;
}

/** Total count of non-cascade staged changes — drives the publish-bar label. */
function summaryCount(s: SectionChangesSummary): number {
  return (
    s.taktifications.length +
    s.zone_sets.length +
    s.work_code_adds.length +
    s.zone_retags.length +
    s.untaktifications.length +
    (s.zone_order.length > 0 ? 1 : 0) +
    s.new_activities.length +
    s.deleted_activities.length +
    s.renames.length +
    s.work_code_removals.length +
    s.work_code_edits.length +
    s.work_code_deletes.length +
    (s.section_claims ?? []).length +
    s.dep_edits.add + s.dep_edits.remove + s.dep_edits.update
  );
}

const EMPTY_SET: Set<number> = new Set();
const noop = () => undefined;
/** Read-only AnchorsBoard: no edits land, so every intent is a no-op. */
const NO_INTENTS = {
  onAddEdge: noop,
  onRemoveEdge: noop,
  onRetypeEdge: noop,
  onReplaceEdge: noop,
};

type Tab = "changes" | "cascade" | "anchors";

const PublishSectionModal: React.FC<Props> = ({
  sectionTitle,
  headerLeft,
  subtitle,
  anchorStats,
  anchorGraph,
  onEditAnchors,
  preview,
  ledger,
  changesOnly = true,
  onChangesOnlyChange,
  colors,
  activityWorkCodeMap,
  zoneMap,
  publishing,
  onPublish,
  onCancel,
}) => {
  const { cascade_result, section_changes_summary: s } = preview;
  const changeCount = summaryCount(s);
  const rippleCount = React.useMemo(
    () => downstreamCount(cascade_result),
    [cascade_result],
  );
  const severed = anchorStats?.severed ?? [];
  const undecidedCount = (s.left_behind ?? []).filter(
    (e) => e.state === "unresolved",
  ).length;

  // Section runs later than before? (ISO datetimes sort lexically.)
  const sd = preview.section_date_summary;
  const laterThan = (a: string | null, b: string | null) => !!(a && b && a > b);
  const startLater = sd ? laterThan(sd.after_start, sd.before_start) : false;
  const finishLater = sd ? laterThan(sd.after_finish, sd.before_finish) : false;

  const [tab, setTab] = React.useState<Tab>("changes");
  const [depsConfirmed, setDepsConfirmed] = React.useState(false);
  // Defer the downstream ripple — save the plan only, resolve later via F9.
  const [ignoreCascade, setIgnoreCascade] = React.useState(false);

  // The "What changed" table groups by WBS leaf; collapse state lives here.
  const [collapsed, setCollapsed] = React.useState<Set<string>>(() => new Set());
  const toggleGroup = React.useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const cascadeWb = React.useMemo(
    () => downstreamCascadeToWorkbench(cascade_result, activityWorkCodeMap),
    [cascade_result, activityWorkCodeMap],
  );
  // The cascade rows are OUTSIDE the pulled section, so the section/zone maps
  // from the board don't cover them — feed the WBS breadcrumb as the section
  // line so each row shows its own leaf under the name.
  const cascadeSectionMap = React.useMemo(
    () => downstreamSectionMap(cascade_result),
    [cascade_result],
  );

  // Only force the dependency confirmation when this draft actually has ties to
  // ADDRESS — i.e. it severed an outside tie (the work needs re-homing). A plan
  // that merely touches outside work without cutting anything needs no gate.
  const requireConfirm = severed.length > 0;
  const publishBlocked = requireConfirm && !depsConfirmed;

  return (
    <div className="publish-overlay">
      <div className="pull-plan-topbar publish-reviewbar">
        {headerLeft}
        <span className="pull-plan-topbar-div" aria-hidden="true" />
        <h2>{sectionTitle}</h2>
        <span className="pull-plan-topbar-draftchip">PULL DRAFT</span>
        {subtitle && (
          <span className="pull-plan-topbar-subtitle">{subtitle}</span>
        )}
        <span className="publish-reviewbar-title">Review &amp; Publish</span>
      </div>

      <div className="publish-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={"publish-tab" + (tab === "changes" ? " is-active" : "")}
          onClick={() => setTab("changes")}
        >
          ▤ What changed
          <span className="publish-tab-badge">{changeCount}</span>
        </button>
        <button
          type="button"
          role="tab"
          className={"publish-tab" + (tab === "cascade" ? " is-active" : "")}
          onClick={() => setTab("cascade")}
        >
          ↗ Cascade
          {rippleCount > 0 && (
            <span className="publish-tab-badge publish-tab-badge--alert">
              ⚠ {rippleCount}
            </span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          className={"publish-tab" + (tab === "anchors" ? " is-active" : "")}
          onClick={() => setTab("anchors")}
        >
          ⚓ Anchors
          <span
            className={
              "publish-tab-badge" +
              (severed.length > 0 ? " publish-tab-badge--alert" : "")
            }
          >
            {severed.length}
          </span>
        </button>
      </div>

      <div className="publish-tabbody">
        {tab === "changes" && (
          <div className="publish-pane publish-pane--changes">
            <div className="publish-pane-head">
              {(startLater || finishLater) && sd ? (
                <div className="publish-pane-flag">
                  ⚠ Pushes the section <b>later</b>:{" "}
                  {finishLater ? (
                    <>
                      finish {formatDateMonthDay(sd.before_finish)} →{" "}
                      <b>{formatDateMonthDay(sd.after_finish)}</b>
                    </>
                  ) : (
                    <>
                      start {formatDateMonthDay(sd.before_start)} →{" "}
                      <b>{formatDateMonthDay(sd.after_start)}</b>
                    </>
                  )}
                </div>
              ) : (
                <span />
              )}
              {onChangesOnlyChange && (
                <div className="publish-seg" role="group" aria-label="Row filter">
                  <button
                    type="button"
                    className={changesOnly ? "publish-seg-btn--active" : ""}
                    onClick={() => onChangesOnlyChange(true)}
                  >
                    Changes only
                  </button>
                  <button
                    type="button"
                    className={!changesOnly ? "publish-seg-btn--active" : ""}
                    onClick={() => onChangesOnlyChange(false)}
                  >
                    Show all
                  </button>
                </div>
              )}
            </div>
            <div className="publish-changes-scroll">
              {ledger && ledger.members.length > 0 ? (
                <LedgerTable
                  members={ledger.members}
                  rowFacts={ledger.rowFacts}
                  anchorDir={ledger.anchorDir}
                  showYear={ledger.showYear}
                  collapsedGroups={collapsed}
                  onToggleGroup={toggleGroup}
                  changesOnly={changesOnly}
                />
              ) : (
                <div className="publish-empty-pane">No section activities to show.</div>
              )}
            </div>
          </div>
        )}

        {tab === "cascade" && (
          <div className="publish-pane publish-pane--cascade">
            <div className="publish-cascade-note">
              {rippleCount > 0 ? (
                <>
                  <b>{rippleCount}</b> downstream{" "}
                  {rippleCount === 1 ? "activity" : "activities"} outside this plan
                  will shift. The <b>Predecessors</b> column shows{" "}
                  <i>what drove</i> each one.
                </>
              ) : (
                "Nothing downstream of this plan moves."
              )}
            </div>
            <label className="publish-ignore">
              <input
                type="checkbox"
                checked={ignoreCascade}
                onChange={(e) => setIgnoreCascade(e.target.checked)}
                disabled={rippleCount === 0}
              />
              <span>
                <b>Ignore downstream — save the plan only.</b> Publishes this
                plan&apos;s work and leaves the {rippleCount} downstream{" "}
                {rippleCount === 1 ? "activity" : "activities"} at their current
                dates, to resolve later with Update Schedule.
              </span>
            </label>
            {rippleCount > 0 ? (
              <div className="publish-cascade-rows">
                <WorkbenchRows
                  result={cascadeWb}
                  colors={colors}
                  activityWorkCodeMap={activityWorkCodeMap}
                  sectionMap={cascadeSectionMap}
                  zoneMap={zoneMap}
                  readOnly
                  editedEdgeIds={EMPTY_SET}
                  editedDurationIds={EMPTY_SET}
                  violatedIds={EMPTY_SET}
                  onEdgeChange={noop}
                  onEdgeRemove={noop}
                  onDurationChange={noop}
                />
              </div>
            ) : (
              <div className="publish-empty-pane">
                No downstream schedule dates change from this publish.
              </div>
            )}
          </div>
        )}

        {tab === "anchors" && (
          <div className="publish-pane publish-pane--anchors">
            <div className="publish-anchors-top">
              {requireConfirm && (
                <label
                  className={
                    "publish-anchors-ack" +
                    (depsConfirmed ? " publish-anchors-ack--done" : "")
                  }
                >
                  <input
                    type="checkbox"
                    checked={depsConfirmed}
                    onChange={(e) => setDepsConfirmed(e.target.checked)}
                  />
                  Confirm that all incoming and outgoing dependencies are correct.
                </label>
              )}
              {onEditAnchors && severed.length > 0 && (
                <button
                  type="button"
                  className="publish-anchors-edit"
                  onClick={onEditAnchors}
                >
                  Edit anchors on the board →
                </button>
              )}
            </div>
            {undecidedCount > 0 && (
              <div className="publish-banner publish-banner--warn">
                ⚠ {undecidedCount} activit{undecidedCount === 1 ? "y" : "ies"} in
                the pulled nodes {undecidedCount === 1 ? "has" : "have"} no
                decision yet — {undecidedCount === 1 ? "it keeps" : "they keep"}{" "}
                {undecidedCount === 1 ? "its" : "their"} current P6 dates.
              </div>
            )}
            {anchorGraph &&
            Object.keys(anchorGraph.activities ?? {}).length > 0 ? (
              <div className="publish-anchors-board">
                <AnchorsBoard
                  graph={anchorGraph}
                  loading={false}
                  error={null}
                  readOnly
                  intents={NO_INTENTS}
                />
              </div>
            ) : (
              <div className="publish-empty-pane">
                No outside ties are affected by this plan.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="publish-actions">
        <div className="publish-actions-summary">
          <b>{changeCount} change{changeCount === 1 ? "" : "s"}</b>
          {severed.length > 0 && (
            <span className="publish-summ-cut">
              {" · "}
              {severed.length} outside tie{severed.length === 1 ? "" : "s"} cut
            </span>
          )}
          {rippleCount > 0 && (
            <span className="publish-muted">
              {" · "}
              {ignoreCascade
                ? `${rippleCount} downstream deferred`
                : `${rippleCount} downstream shift${rippleCount === 1 ? "" : "s"}`}
            </span>
          )}
        </div>
        <div className="publish-actions-btns">
          {publishBlocked && (
            <button
              type="button"
              className="publish-blocked-msg"
              onClick={() => setTab("anchors")}
              title="Go to the Anchors tab"
            >
              ⚓ Confirm the dependencies on the <b>Anchors</b> tab to publish
            </button>
          )}
          <button className="publish-cancel" onClick={onCancel} disabled={publishing}>
            Cancel
          </button>
          <button
            className="publish-confirm"
            onClick={() => onPublish(!ignoreCascade)}
            disabled={publishing || publishBlocked}
            title={
              publishBlocked
                ? "Confirm the dependencies on the Anchors tab first"
                : undefined
            }
          >
            {publishing
              ? "Publishing…"
              : ignoreCascade
                ? "Publish plan only →"
                : "Publish to schedule →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishSectionModal;
